import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftIcon,
  UserIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import {
  getDocumentById,
  countDownloadDocument,
} from "../../../services/DocumentService";
import { shareContent } from "../../../utils/share";
import { Share2, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import { checkJwtExistsAndExpired } from "../../../services/AuthService";

const MaterialDetailPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = checkJwtExistsAndExpired();

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await getDocumentById(documentId);
        setDocument(response.data);
      } catch (error) {
        toast.error("Không tìm thấy tài liệu");
        navigate("/materials");
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, [documentId, navigate]);

  const embedUrl = useMemo(() => {
    if (!document?.url) return "";
    const driveFileRegex = /https?:\/\/drive\.google\.com\/file\/d\/([^/]+)/;
    const downloadRegex = /id=([^&]+)/;

    const driveMatch = document.url.match(driveFileRegex);
    if (driveMatch?.[1]) {
      return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    }
    const downloadMatch = document.url.match(downloadRegex);
    if (downloadMatch?.[1]) {
      return `https://drive.google.com/file/d/${downloadMatch[1]}/preview`;
    }
    return document.url;
  }, [document?.url]);

  const detailUrl = useMemo(() => {
    if (!document?._id) return window.location.origin;
    return `${window.location.origin}/materials/${document._id}`;
  }, [document?._id]);

  const handleShare = () => {
    shareContent({ title: document?.title, url: detailUrl });
  };

  const handleOpenOriginal = async () => {
    if (!document?.url) return;
    if (!auth) {
      toast.error("Vui lòng đăng nhập để xem tài liệu");
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    try {
      await countDownloadDocument(document._id);
    } catch (error) {
      console.error("Error counting download:", error);
    } finally {
      window.open(document.url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="bg-[#f9dfe8] min-h-screen py-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="h-10 w-40 bg-white/70 rounded mb-6 animate-pulse" />
          <div className="bg-white/80 rounded-3xl shadow-2xl p-8 animate-pulse h-[70vh]" />
        </div>
      </div>
    );
  }

  if (!document) {
    return null;
  }

  return (
    <div className="bg-[#f9dfe8] min-h-screen py-10">
      <div className="max-w-5xl mx-auto px-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-[#db3b6d] font-semibold mb-6 hover:text-[#a92e55] transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Quay lại
        </button>

        <div className="bg-white rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
          <div className="bg-gradient-to-r from-[#db3b6d] to-[#a92e55] p-8 text-white relative">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="uppercase text-xs tracking-[0.3em] text-white/80 mb-2">
                  Tài liệu
                </p>
                <h1 className="text-3xl md:text-4xl font-extrabold leading-snug">
                  {document.title}
                </h1>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleShare}
                  className="w-12 h-12 rounded-2xl bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                  aria-label="Chia sẻ"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button
                  onClick={handleOpenOriginal}
                  className="w-12 h-12 rounded-2xl bg-white text-[#db3b6d] flex items-center justify-center shadow-lg hover:shadow-xl transition-all"
                  aria-label="Mở tài liệu gốc"
                >
                  <ExternalLink className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                <div className="text-sm">
                  <p className="text-white/70 text-xs">Người đăng</p>
                  <p className="font-semibold">
                    {document?.createdBy?.fullName || "PIMAX Education"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                <div className="text-sm">
                  <p className="text-white/70 text-xs">Ngày tạo</p>
                  <p className="font-semibold">
                    {document.createdAt
                      ? dayjs(document.createdAt).format("DD/MM/YYYY")
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ArrowDownTrayIcon className="w-5 h-5" />
                <div className="text-sm">
                  <p className="text-white/70 text-xs">Lượt tải</p>
                  <p className="font-semibold">
                    {document.numberOfDơwnload || 0}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <EyeIcon className="w-5 h-5" />
                <div className="text-sm">
                  <p className="text-white/70 text-xs">Lượt xem</p>
                  <p className="font-semibold">
                    {document.numberOfVisitors || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            <div className="bg-[#fde7ee] rounded-2xl p-6 border border-[#f0bccc]">
              <h2 className="text-lg font-bold text-[#db3b6d] mb-3">
                Giới thiệu tài liệu
              </h2>
              <p className="text-[#4b4b4b] leading-relaxed whitespace-pre-line">
                {document.description || "Chưa có mô tả cho tài liệu này."}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg">
              <div className="bg-[#f9f9f9] border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <span className="font-semibold text-[#4b4b4b]">
                  Xem trước tài liệu
                </span>
                {document.url && (
                  <a
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#db3b6d] hover:text-[#a92e55] font-medium"
                  >
                    Mở Google Drive
                  </a>
                )}
              </div>
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title={document.title}
                  width="100%"
                  height="480"
                  allow="autoplay"
                  className="border-0"
                ></iframe>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Không thể hiển thị preview cho tài liệu này.
                </div>
              )}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#db3b6d] text-white font-semibold hover:bg-[#a92e55] transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Chia sẻ tài liệu
              </button>
              <button
                onClick={handleOpenOriginal}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[#db3b6d] text-[#db3b6d] font-semibold hover:bg-[#db3b6d] hover:text-white transition-colors"
              >
                <ExternalLink className="w-5 h-5" />
                Mở tài liệu gốc
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialDetailPage;
